import type { SpacingToken, SemanticColorToken, BorderWidthToken, RadiusToken } from '../../../themes/types';
import type { FontToken, FontProperties } from '../../../typography/types';
import { resolveColorsStyleCss, resolveBackgroundColorsStyleCss } from './colors';
import { resolveFontStyleCss, resolveTextDecorationStyleCss } from './font';
import { resolvePaddingStyleCss, resolveMarginStyleCss, resolveLayoutStyleCss } from './layout';
import { resolveDisplayStyleCss } from './display';
import { resolveDimensionsStyleCss } from './dimensions';
import { resolvePositioningStyleCss } from './position';
import { resolveOverflowStyleCss } from './overflow';
import { resolveFlexStyleCss } from './flex';
import { resolveBoxMiscStyleCss } from './boxMisc';
import { resolveScrollbarStyleCss } from './scrollbar';

// ============================================================================
// Shared types
// ============================================================================

export type StyleGroup =
  | 'colors'
  | 'font'
  | 'padding'
  | 'margin'
  | 'textDecoration'
  | 'display'
  | 'backgroundColors'
  | 'dimensions'
  | 'positioning'
  | 'layout'
  | 'overflow'
  | 'flex'
  | 'boxMisc'
  | 'scrollbar';

/** Theme helpers passed to style pickers (same shape as useTheme() return). */
export interface StyleResolverContext {
  getColor: (token: SemanticColorToken) => string;
  getFont: (fontToken: FontToken) => FontProperties;
  getSpacing: (token: SpacingToken) => string;
  getRadius: (token: RadiusToken) => string;
  getLayout: (layoutProperty: string, token: string) => string | number;
  getBorderWidth: (token: BorderWidthToken) => string;
}

// ============================================================================
// Registry
// ============================================================================

/** A single CSS declaration or null (filtered out by Emotion). */
export type CssFragment = string | null;

export type StylePicker = (props: Record<string, unknown>, theme: StyleResolverContext) => CssFragment[];

export const STYLE_RESOLVERS: Record<StyleGroup, StylePicker> = {
  colors: resolveColorsStyleCss,
  font: resolveFontStyleCss,
  padding: resolvePaddingStyleCss,
  margin: resolveMarginStyleCss,
  textDecoration: resolveTextDecorationStyleCss,
  display: resolveDisplayStyleCss,
  backgroundColors: resolveBackgroundColorsStyleCss,
  dimensions: resolveDimensionsStyleCss,
  positioning: resolvePositioningStyleCss,
  layout: resolveLayoutStyleCss,
  overflow: resolveOverflowStyleCss,
  flex: resolveFlexStyleCss,
  boxMisc: resolveBoxMiscStyleCss,
  scrollbar: resolveScrollbarStyleCss,
};

// ============================================================================
// Re-exports for consumers (CoreBox, CoreText, etc.)
// ============================================================================

export { resolveSpacingValue } from './layout';
export type { SpacingValue, MarginValue, PaddingProps, MarginProps } from './layout';
export type { RadiusProps, LayoutProps } from './layout';
export type { ColorProps } from './colors';
export type { BackgroundColorsProps } from './colors';
export type { FontProps, TextDecorationProps } from './font';
export type { DimensionProps } from './dimensions';
export type { DisplayFlexProps } from './display';
export type { PositioningProps } from './position';
export type { OverflowProps, OverflowFlexProps } from './overflow';
export type { FlexProps } from './flex';
export type { BoxMiscProps } from './boxMisc';
